// Client-safe SEO metadata: static per-route entries plus small builders for
// data-driven routes. This module must NOT import src/data/news or
// src/data/work - it is bundled into the eager main chunk (via Seo.tsx), and
// importing the data files would drag every article/project body into the
// initial JS payload. The full resolver lives in seoMeta.ts (server + lazy
// client use only).

export const SITE_URL = "https://blackswanproductions.co.uk";

export type ResolvedMeta = {
  title: string;
  description: string;
  // Path to the social image, relative to the site base (no leading slash).
  imagePath: string;
  // Plain-text primary heading, mirrored into the server-rendered shell so
  // non-JS crawlers and SEO audits always find an <h1> on every route.
  h1: string;
};

const DEFAULT_IMAGE = "brand/og.jpg";

export const DEFAULT_META: ResolvedMeta = {
  title: "Video Production Agency | Cardiff and South Wales",
  description:
    "Black Swan Productions is an award-winning creative video production agency in Cardiff that enhances purpose-led brands.",
  imagePath: DEFAULT_IMAGE,
  h1: "Proud Welsh Content Producers",
};

const STATIC_META: Record<string, Omit<ResolvedMeta, "imagePath">> = {
  "/": {
    title: DEFAULT_META.title,
    description: DEFAULT_META.description,
    h1: "Proud Welsh Content Producers",
  },
  "/about": {
    title: "Black Swan Productions | Video Production Agency | Cardiff",
    description:
      "Black Swan Productions is a video production agency based in Cardiff, that works with clients across the UK.",
    h1: "About Us",
  },
  "/brand-stand": {
    title: "Brand Stand | Video Content & Thought Leadership | Black Swan",
    description:
      "Brand Stand is our flagship service, producing consistent long-form and short-form video content that builds authority and lasting thought leadership for brands.",
    h1: "Brand Stand",
  },
  "/creative-marketing": {
    title: "Creative & Marketing | Black Swan Productions",
    description:
      "Discover Black Swan Agency, bringing creative thinking, strategic marketing and AI-informed approaches together to help ambitious brands build visibility, influence and growth.",
    h1: "Creative thinking. Intelligent marketing.",
  },
  "/legacy-forge": {
    title: "Legacy Forge | Personal Brand Building | Black Swan",
    description:
      "Legacy Forge supercharges the personal brands and online identities of notable individuals looking to stand out as a leading voice in their space.",
    h1: "Legacy Forge",
  },
  "/de-novo-solutions": {
    title: "de Novo Solutions | Technology Video Case Study | Black Swan",
    description:
      "A long term video partnership with de Novo Solutions, one of Wales' fastest growing technology companies, spanning event films, announcements and recruitment.",
    h1: "de Novo Solutions",
  },
  "/work": {
    title: "Video Production Company in Cardiff & South Wales",
    description:
      "We are a video production company that thrives on producing impactful brand stories for purpose-led brands.",
    h1: "Check out our exciting video projects",
  },
  "/success-stories": {
    title: "Video Success Stories",
    description:
      "We've worked with some excellent brands to bring their stories to life through video. Check out our video success stories with them.",
    h1: "Success Stories",
  },
  "/news": {
    title: "Content Hub - Black Swan Productions - Educational Content",
    description:
      "We produce educational content in the form of both educational videos and written blogs, alongside the Black Swan Podcast.",
    h1: "The Latest News at Black Swan",
  },
  "/indie-film": {
    title: "Independent Films | South Wales Film Production Company",
    description:
      "We're a film production company that produces unique independent psychological thriller films with our filmmakers and extended team.",
    h1: "Black Swan Films",
  },
  "/vertical-micro-dramas": {
    title: "Vertical Micro Dramas | Black Swan Verticals",
    description:
      "Black Swan Verticals is the first Welsh vertical micro drama company, producing genre-driven vertical micro dramas with original IP.",
    h1: "Black Swan Verticals",
  },
  "/memory-wall": {
    title: "Memory Wall | Behind The Scenes | Black Swan Productions",
    description:
      "A storyboard of the people, sets, awards and unscripted moments behind Black Swan Productions, the Cardiff video and film production team.",
    h1: "Memory Wall",
  },
  "/contact": {
    title: "Production Company | Contact Us",
    description:
      "Black Swan Productions is a production company in Cardiff that produces branded content and original content.",
    h1: "Contact Us",
  },
  "/terms-and-conditions": {
    title: "Terms and Conditions | Black Swan Productions",
    description:
      "The terms and conditions for using the Black Swan Productions website.",
    h1: "Terms and Conditions",
  },
  "/terms-and-conditions-supply-of-services": {
    title: "Terms and Conditions - Supply of Services | Black Swan Productions",
    description:
      "The terms and conditions for the supply of video production services by Black Swan Productions Ltd.",
    h1: "Terms and Conditions - Supply of Services",
  },
  "/privacy-policy": {
    title: "Privacy Policy | Black Swan Productions",
    description:
      "How Black Swan Productions Ltd collects, uses and protects your personal data, and your rights under UK data protection law.",
    h1: "Privacy Policy",
  },
};

export function toExcerpt(text: string, max = 155): string {
  const clean = text.replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  const truncated = clean.slice(0, max);
  const lastSpace = truncated.lastIndexOf(" ");
  return `${truncated.slice(0, lastSpace > 0 ? lastSpace : max).trim()}...`;
}

export function normalizePath(rawPath: string): string {
  return rawPath.length > 1 && rawPath.endsWith("/")
    ? rawPath.slice(0, -1)
    : rawPath;
}

export function matchNewsSlug(path: string): string | null {
  return path.match(/^\/news\/([^/]+)$/)?.[1] ?? null;
}

export function matchWorkSlug(path: string): string | null {
  return path.match(/^\/work\/([^/]+)$/)?.[1] ?? null;
}

// Structural parameter types so builders work for both the real data types
// and without importing the data modules.
type NewsMetaSource = {
  title: string;
  cover: string;
  body: readonly (string | { quote: string; attribution: string })[];
};

type WorkMetaSource = {
  title: string;
  excerpt: string;
  cover: string;
};

export function buildNewsMeta(article: NewsMetaSource): ResolvedMeta {
  const firstText = article.body.find((b): b is string => typeof b === "string");
  return {
    title: `${article.title} | Black Swan Productions`,
    description: toExcerpt(firstText ?? DEFAULT_META.description),
    imagePath: article.cover,
    h1: article.title,
  };
}

export function buildWorkMeta(project: WorkMetaSource): ResolvedMeta {
  return {
    title: `${project.title} | Work | Black Swan Productions`,
    description: toExcerpt(project.excerpt),
    imagePath: project.cover,
    h1: project.title,
  };
}

// Synchronous resolver for the eager client bundle. Returns null on
// data-driven detail routes (/news/:slug, /work/:slug); callers resolve those
// asynchronously via dynamic import of the data modules.
export function resolveStaticMeta(rawPath: string): ResolvedMeta | null {
  const path = normalizePath(rawPath);
  const staticMeta = STATIC_META[path];
  if (staticMeta) return { ...staticMeta, imagePath: DEFAULT_IMAGE };
  if (matchNewsSlug(path) || matchWorkSlug(path)) return null;
  return DEFAULT_META;
}
